<template>
  <div :class="$style.emptyStateContainer">
    <div :class="$style.contentWrapper">
      <div :class="$style.pillContainer">
        <HgBadge theme="outlined" :class="$style.pill">
          <HgIcon icon="PriceTag" :color="iconColor" />
          &nbsp;
          {{ t('propertyOverview.emptyState.label1') }}</HgBadge
        >
        <HgBadge theme="outlined" :class="$style.pill">
          <HgIcon icon="Clock" :color="iconColor" />
          &nbsp;
          {{ t('propertyOverview.emptyState.label2') }}</HgBadge
        >
      </div>
      <h2 class="title" :class="$style.title">{{ t('propertyOverview.emptyState.title') }}</h2>
      <p class="description" :class="$style.description">
        {{ t('propertyOverview.emptyState.body') }}
      </p>
      <div :class="$style.buttonWrapper">
        <NewPropertyOnEmptyStateButton>{{
          t('propertyOverview.emptyState.cta')
        }}</NewPropertyOnEmptyStateButton>
      </div>
      <p class="caption" :class="$style.caption">
        {{ t('propertyOverview.emptyState.caption') }}
      </p>
      <img :src="icon" alt="No Data" :class="$style.icon" loading="lazy" />
    </div>
  </div>
</template>
<script lang="ts" setup>
import NewPropertyOnEmptyStateButton from '@/components/common/NewPropertyOnEmptyStateButton/NewPropertyOnEmptyStateButton.vue';
import { useEmptyStateSurvey } from '@/composables/useEmptyStateSurvey';
import { getCurrentPlatform } from '@/helpers/Platform/Platform';
import { HgBadge, HgIcon, SupportedPlatforms } from '@smg-real-estate/domus';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();

const icon = ref('');

const iconColor = computed(() =>
  getCurrentPlatform() === SupportedPlatforms.hg ? 'secondary' : 'outlined',
);

const getImage = async (): Promise<void> => {
  const loadIcon = await import(
    `@/assets/images/how-my-properties-empty-state-price-graph-${locale.value}.png`
  );
  icon.value = loadIcon.default;
};

watch(locale, getImage, { immediate: true });

useEmptyStateSurvey();
</script>
<style lang="scss" module>
@use '@/assets/variables' as *;

.emptyStateContainer {
  margin-bottom: 5.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: $space-75;
  border-radius: $border-radius-l;

  margin-top: 0;
}

.contentWrapper {
  width: 37rem;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $space-100;
}

.icon {
  box-shadow: 0px 0px 4px 7px $color-grey-30;
}

.title {
  margin-top: $space-75;
  margin-bottom: 0;
  text-align: center;
}
.caption {
  @include caption;
  text-align: center;
  font-size: var(--typo-body-3-font-size);
  color: #000;
  font-weight: 700;
}

.description {
  @include body-1;
  margin-top: $space-25;
  margin-bottom: $space-75;
  text-align: center;
  max-width: 32rem;
  font-weight: 400;
  color: #595959;
}

.bodyText {
  @include caption;
  text-align: center;
}

.buttonWrapper {
  width: calc(100% - #{$space-200} * 2);
}

.pillContainer {
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: $space-100;
}

.pill {
  border-radius: 1em;
  font-weight: var(--typo-font-weight-light);
  font-size: var(--typo-body-3-font-size);
  border-color: $color-grey-60 !important;
  background-color: $color-grey-30 !important;
}
</style>
